import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, Radio, Video } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");

/** Next Friday/Saturday live class with a countdown and a Join button. */
export const NextLiveClass = () => {
  const [next, setNext] = useState(undefined); // undefined = loading, null = none
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    api.get("/yoga/live-sessions")
      .then(({ data }) => setNext((data.upcoming || [])[0] || null))
      .catch(() => setNext(null));
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const countdown = useMemo(() => {
    if (!next?.scheduled_at) return null;
    const start = new Date(next.scheduled_at).getTime();
    const diff = start - now;
    const live = diff <= 0 && now - start < (next.duration_minutes || 60) * 60000;
    if (live) return { live: true, label: "Live now" };
    if (diff <= 0) return { live: false, label: "Just finished" };
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return { live: false, label: d > 0 ? `${d}d ${pad(h)}h ${pad(m)}m` : `${pad(h)}:${pad(m)}:${pad(s)}` };
  }, [next, now]);

  if (next === undefined) return null;

  return (
    <Card className="border-border" data-testid="next-live-class">
      <CardContent className="p-6 lg:p-8">
        {next === null ? (
          <div data-testid="next-live-empty">
            <h3 className="font-display text-2xl flex items-center gap-2"><CalendarClock size={18}/> Next live class</h3>
            <p className="text-sm text-muted-foreground mt-2">
              No live class is on the calendar yet. Your daily practice is always available — we will post the
              next Friday and Saturday session here as soon as it is scheduled.
            </p>
            <Link to="/yoga/dashboard#lessons">
              <Button variant="outline" className="btn-pill mt-5" data-testid="next-live-empty-cta">
                <Video size={14} className="mr-1.5"/> Continue today&apos;s practice
              </Button>
            </Link>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-5">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="overline">Next live class</span>
                {countdown?.live && (
                  <Badge className="bg-destructive text-destructive-foreground text-[10px] animate-pulse" data-testid="next-live-badge">
                    <Radio size={10} className="mr-1"/> LIVE
                  </Badge>
                )}
              </div>
              <div className="font-display text-2xl mt-1" data-testid="next-live-title">{next.title}</div>
              <div className="text-sm text-muted-foreground mt-1">
                {new Date(next.scheduled_at).toLocaleString(undefined, {
                  weekday: "long", day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
                })} · {next.duration_minutes} min
              </div>
              {next.description && <p className="text-xs text-muted-foreground mt-2 max-w-lg">{next.description}</p>}
            </div>

            <div className="text-right">
              <div className="font-display text-3xl tabular-nums" data-testid="next-live-countdown">{countdown?.label}</div>
              <Button
                disabled={!countdown?.live || !next.join_url}
                onClick={() => window.open(next.join_url, "_blank")}
                className="btn-pill bg-primary text-primary-foreground mt-3"
                data-testid="next-live-join"
              >
                {countdown?.live ? "Join now" : "Join opens at class time"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NextLiveClass;
